import {
  IonItem,
  IonLabel,
  IonButton,
  IonModal,
  IonContent,
  IonSearchbar,
  IonList,
  IonCheckbox,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonText,
} from "@ionic/react";
import { Controller } from "react-hook-form";
import type { Control, FieldValues, FieldPath } from "react-hook-form";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { getMaterialsPaginated, type Material } from "@/api";
import React, { useState, useMemo } from "react";

import "./SelectMaterial.css";
import { AppHeader } from "./AppHeader";
import { Did } from "@/types";

// Props for the input component
interface SelectMaterialInputProps {
  value?: Did;
  disabled?: boolean;
  onMaterialSelect: (material: Material) => void;
  selectedMaterial?: Material;
  className?: string;
  children?: (props: {
    onClick: () => void;
    disabled?: boolean;
  }) => React.ReactNode;
}

// Props for the control wrapper
interface SelectMaterialControlProps<T extends FieldValues> {
  control: Control<T>;
  required: boolean;
  name: FieldPath<T>;
  disabled: boolean;
  className?: string;
}

// The input component without form control logic
const SelectMaterialInput: React.FC<SelectMaterialInputProps> = ({
  children,
  value,
  className,
  disabled,
  onMaterialSelect,
  selectedMaterial: selectedMaterialProp,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const queryClient = useQueryClient();

  const getSelectedMaterial = (
    selectedId: Did | null | undefined
  ): Material | undefined => {
    if (!selectedId) return undefined;

    // If selectedMaterial is passed as prop, use it
    if (selectedMaterialProp && selectedMaterialProp.id === selectedId) {
      return selectedMaterialProp;
    }

    // First try to find in current loaded pages
    const found = allMaterials.find((p) => p.id === selectedId);
    if (found) return found;

    // If not found, check React Query cache for individual material
    const cached = queryClient.getQueryData(["material", selectedId]);
    return cached as Material | undefined;
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ["materials", "paginated", searchText],
      queryFn: ({ pageParam = 1 }) =>
        getMaterialsPaginated({
          page: pageParam,
          search: searchText || undefined,
        }),
      getNextPageParam: (lastPage) => {
        return lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined;
      },
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

  const allMaterials = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) ?? [];
  }, [data]);

  const handleMaterialSelect = (material: Material) => {
    // Cache the selected material data
    queryClient.setQueryData(["material", material.id], material);
    onMaterialSelect(material);
    setIsOpen(false);
    setSearchText("");
  };

  const selectedMaterial = getSelectedMaterial(value);

  const handleKeepCurrent = () => {
    if (selectedMaterial) {
      onMaterialSelect(selectedMaterial);
      setIsOpen(false);
      setSearchText("");
    }
  };

  return (
    <>
      {children ? (
        children({ onClick: () => setIsOpen(true), disabled })
      ) : (
        <IonItem className={className}>
          <IonLabel position="stacked">Materiaal</IonLabel>
          <IonButton
            fill="outline"
            expand="block"
            disabled={disabled}
            onClick={() => setIsOpen(true)}
          >
            {selectedMaterial ? selectedMaterial.name : "Selecteer material"}
          </IonButton>
        </IonItem>
      )}
      <IonModal
        isOpen={isOpen}
        onDidDismiss={() => setIsOpen(false)}
        className="select-material-modal"
      >
        <AppHeader
          title="Selecteer Material"
          onClose={() => setIsOpen(false)}
        />

        <IonContent>
          <div
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Fixed header area */}
            <div>
              <IonSearchbar
                value={searchText}
                onIonInput={(e) => setSearchText(e.detail.value!)}
                placeholder="Zoek material..."
              />

              {/* Currently selected material */}
              {selectedMaterial && (
                <>
                  <IonItem color="light" lines="none">
                    <IonCheckbox slot="start" checked={true} />
                    <IonLabel>
                      <p className="ion-color-medium">Currently selected</p>
                      <h3 className="ion-text-wrap ion-text-bold">
                        {selectedMaterial.name}
                      </h3>
                    </IonLabel>
                    <IonButton
                      slot="end"
                      fill="outline"
                      size="small"
                      onClick={handleKeepCurrent}
                    >
                      Keep
                    </IonButton>
                  </IonItem>
                  <div
                    className="ion-margin-horizontal"
                    style={{
                      height: "1px",
                      backgroundColor: "var(--ion-color-light-shade)",
                    }}
                  />
                </>
              )}
            </div>

            {/* Scrollable material list */}
            <div style={{ flex: 1, overflow: "hidden" }}>
              <IonContent scrollEvents={true}>
                <IonList className="bg-none">
                  {allMaterials.map((material) => (
                    <IonItem
                      key={material.id}
                      button
                      onClick={() => handleMaterialSelect(material)}
                    >
                      <IonCheckbox
                        className="ion-margin-end"
                        slot="start"
                        checked={value === material.id}
                      />
                      <IonLabel>
                        <h3>{material.name}</h3>
                      </IonLabel>
                    </IonItem>
                  ))}

                  {allMaterials.length === 0 && !isLoading && (
                    <IonItem>
                      <IonLabel>
                        <IonText color="medium">
                          {searchText
                            ? "Geen materialen gevonden"
                            : "Geen materialen beschikbaar"}
                        </IonText>
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>

                <IonInfiniteScroll
                  onIonInfinite={async (ev) => {
                    if (hasNextPage && !isFetchingNextPage) {
                      await fetchNextPage();
                    }
                    ev.target.complete();
                  }}
                  disabled={!hasNextPage}
                >
                  <IonInfiniteScrollContent
                    loadingText="Laden..."
                    loadingSpinner="bubbles"
                  />
                </IonInfiniteScroll>
              </IonContent>
            </div>
          </div>
        </IonContent>
      </IonModal>
    </>
  );
};

// The control wrapper component
const SelectMaterialControl = <T extends FieldValues>({
  control,
  className,
  required,
  name,
  disabled,
}: SelectMaterialControlProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: "Selecteer een materiaal" } : {}}
      render={({ field }) => (
        <SelectMaterialInput
          className={className}
          value={field.value}
          disabled={disabled}
          onMaterialSelect={field.onChange}
        />
      )}
    />
  );
};

export { SelectMaterialInput, SelectMaterialControl };
